import { supabase } from '../supabase';
import { toast } from 'react-hot-toast';

// Create a global cache to track pending like operations
// This helps prevent race conditions when rapidly clicking like buttons
const pendingLikes = new Set<string>();

/**
 * Toggle like status for a snap and update the database
 * @param snapId The ID of the snap to toggle like status for
 * @returns An object with the new like status and count
 */
export async function toggleLike(snapId: string): Promise<{ liked: boolean; count: number }> {
  try {
    // Check if there's already a pending operation for this snap
    if (pendingLikes.has(snapId)) {
      // console.log('Like operation already in progress for snap:', snapId);
      throw new Error('Please wait before toggling like again');
    }

    // Add this snap to pending operations
    pendingLikes.add(snapId);

    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Check if user already liked this snap
    const { data: existingLike } = await supabase
      .from('snap_likes')
      .select('id')
      .eq('snap_id', snapId)
      .eq('user_id', user.id)
      .maybeSingle();

    // Toggle like status
    if (existingLike) {
      // Unlike: Delete the like
      const { error } = await supabase
        .from('snap_likes')
        .delete()
        .eq('snap_id', snapId)
        .eq('user_id', user.id);

      if (error) throw error;
    } else {
      // Like: Insert new like
      const { error } = await supabase
        .from('snap_likes')
        .insert({ snap_id: snapId, user_id: user.id });

      if (error) throw error;
    }

    // Get updated like count
    const { count } = await supabase
      .from('snap_likes')
      .select('*', { count: 'exact', head: true })
      .eq('snap_id', snapId);

    // Return the new like status and count
    return {
      liked: !existingLike,
      count: count || 0
    };
  } catch (error) {
    console.error('Error toggling like:', error);
    throw error;
  } finally {
    // Remove this snap from pending operations
    pendingLikes.delete(snapId);
  }
}

/**
 * Get the current like status and count for a snap
 * @param snapId The ID of the snap to get like status for
 * @returns An object with the current like status and count
 */
export async function getLikeStatus(snapId: string): Promise<{ liked: boolean; count: number }> {
  try {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { liked: false, count: 0 };

    // Get like status and count in parallel
    const [likeStatus, likeCount] = await Promise.all([
      supabase
        .from('snap_likes')
        .select('id')
        .eq('snap_id', snapId)
        .eq('user_id', user.id)
        .maybeSingle(),
      supabase
        .from('snap_likes')
        .select('*', { count: 'exact', head: true })
        .eq('snap_id', snapId)
    ]);

    return {
      liked: !!likeStatus.data,
      count: likeCount.count || 0
    };
  } catch (error) {
    console.error('Error getting like status:', error);
    return { liked: false, count: 0 };
  }
}